/**
 * YG 产品卡片布局切换功能
 * - 桌面端：切换 normal（竖版）和 media（横版）布局
 * - 移动端：切换旧版和新版移动端布局
 */
class YGLayoutToggle extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = 'yg_collection_layout_preference';
    this.MOBILE_STORAGE_KEY = 'yg_mobile_layout_preference';
    this.buttons = null;
    this.mobileButtons = null;
    this.productGrid = null;
    this.currentLayout = 'normal';
    this.currentMobileLayout = null; // 初始为 null，connectedCallback 中根据 backendLayout 决定默认值
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    // 桌面端布局切换按钮
    this.buttons = this.querySelectorAll('.yg-desktop-toggle .yg-layout-toggle-btn');
    // 移动端布局切换按钮
    this.mobileButtons = this.querySelectorAll('.yg-mobile-toggle .yg-layout-toggle-btn');
    this.productGrid = document.querySelector('#product-grid');
    
    if (!this.productGrid) return;

    // 从 data 属性读取后台配置的初始布局
    const backendLayout = this.dataset.initialLayout || 'normal';
    
    // 保存原始的网格类名
    this.originalGridClasses = this.productGrid.className;
    
    // 生成 normal 和 media 两种布局的类名
    this.generateGridClasses();
    
    // 使用事件委托，绑定到组件本身而不是单个按钮
    this.addEventListener('click', this.handleClick);
    
    // 桌面端布局切换
    if (this.buttons.length) {
      // 从 localStorage 读取用户偏好
      this.loadLayoutPreference();
      
      // 如果没有用户偏好，使用后台配置
      if (!this.currentLayout || (this.currentLayout !== 'normal' && this.currentLayout !== 'media')) {
        this.currentLayout = backendLayout;
      }

      // 初始化布局
      this.applyLayout(this.currentLayout, false);
    } else {
      // 未启用桌面端布局切换时，不强制改写网格类名
      this.currentLayout = null;
    }

    // 移动端布局切换
    // 无论是否有切换按钮，都需要初始化 data-yg-mobile-layout 属性
    // 避免 CSS 选择器无法匹配导致卡片样式异常
    this.loadMobileLayoutPreference();
    
    // 如果 localStorage 没有记录，根据后台 initial_layout 决定默认值
    // initial_layout='media' 对应移动端 'new'（横版），否则默认 'old'（竖版）
    if (!this.currentMobileLayout) {
      this.currentMobileLayout = (backendLayout === 'media') ? 'new' : 'old';
    }
    
    // 直接设置属性，不受屏幕宽度限制
    this.productGrid.setAttribute('data-yg-mobile-layout', this.currentMobileLayout);

    if (this.mobileButtons.length) {
      // 更新按钮激活状态
      this.mobileButtons.forEach(btn => {
        if (btn.dataset.mobileLayout === this.currentMobileLayout) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    }

    // 监听窗口 resize，跨越断点时自动切换布局（防抖 150ms）
    this._prevIsMobile = window.innerWidth < 750;
    this._handleResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        const isMobile = window.innerWidth < 750;
        if (isMobile === this._prevIsMobile) return; // 未跨越断点，忽略
        this._prevIsMobile = isMobile;

        if (!this.productGrid) return;

        if (isMobile) {
          // 切换到移动端：按当前 currentMobileLayout 应用移动端布局
          this.mobileButtons = this.querySelectorAll('.yg-mobile-toggle .yg-layout-toggle-btn');
          this.mobileButtons.forEach(btn => {
            if (btn.dataset.mobileLayout === this.currentMobileLayout) {
              btn.classList.add('active');
              btn.setAttribute('aria-pressed', 'true');
            } else {
              btn.classList.remove('active');
              btn.setAttribute('aria-pressed', 'false');
            }
          });
          this.productGrid.setAttribute('data-yg-mobile-layout', this.currentMobileLayout);
        } else {
          // 切换到桌面端：仅在启用桌面端布局切换时才应用
          if (this.buttons.length && this.currentLayout) {
            // 重新生成类名（防止类名引用过期）
            this.originalGridClasses = this.productGrid.className;
            this.generateGridClasses();
            this.applyLayout(this.currentLayout, false);
          }
        }
      }, 150);
    };
    window.addEventListener('resize', this._handleResize);
  }

  disconnectedCallback() {
    // 清理事件监听器
    this.removeEventListener('click', this.handleClick);
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize);
    }
  }

  /**
   * 统一的点击事件处理器（事件委托）
   */
  handleClick(e) {
    const btn = e.target.closest('.yg-layout-toggle-btn');
    if (!btn) return;

    e.preventDefault();

    // 桌面端布局切换
    if (btn.dataset.layout) {
      const targetLayout = btn.dataset.layout;
      this.switchLayout(targetLayout);
    }
    
    // 移动端布局切换
    if (btn.dataset.mobileLayout) {
      const targetLayout = btn.dataset.mobileLayout;
      this.switchMobileLayout(targetLayout);
    }
  }

  /**
   * 生成 normal 和 media 两种布局的类名
   */
  generateGridClasses() {
    const classes = this.originalGridClasses.split(' ').filter(c => c.trim());
    
    // 提取基础类名（不包含布局相关的）
    const baseClasses = classes.filter(cls => 
      !cls.includes('grid-') || 
      cls === 'grid' || 
      cls === 'product-grid'
    );
    
    // 提取移动端列数类
    const mobileColClass = classes.find(cls => 
      cls === 'grid-1-col' || cls === 'grid-2-col'
    ) || 'grid-2-col';
    
    // 提取桌面端列数类
    const desktopColClass = classes.find(cls => 
      cls.includes('grid-4-col-desktop') || 
      cls.includes('grid-5-col-desktop') ||
      cls.includes('grid-3-col-desktop')
    ) || 'grid-4-col-desktop';
    
    const hasCompact = classes.includes('grid-compact');
    
    // Normal 布局类名
    this.normalGridClasses = [
      ...baseClasses,
      mobileColClass,
      'grid-3-col-tablet',
      hasCompact ? 'grid-compact' : '',
      desktopColClass
    ].filter(c => c).join(' ');
    
    // Media 布局类名
    this.mediaGridClasses = [
      ...baseClasses,
      mobileColClass,
      'grid-media-cards',
      'grid-1-col-tablet'
    ].filter(c => c).join(' ');
  }

  /**
   * 加载用户布局偏好（桌面端）
   */
  loadLayoutPreference() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved && (saved === 'normal' || saved === 'media')) {
        this.currentLayout = saved;
      }
    } catch (e) {
      console.warn('无法读取布局偏好:', e);
    }
  }

  /**
   * 保存用户布局偏好（桌面端）
   */
  saveLayoutPreference(layout) {
    try {
      localStorage.setItem(this.STORAGE_KEY, layout);
    } catch (e) {
      console.warn('无法保存布局偏好:', e);
    }
  }

  /**
   * 加载移动端布局偏好
   */
  loadMobileLayoutPreference() {
    try {
      const saved = localStorage.getItem(this.MOBILE_STORAGE_KEY);
      if (saved && (saved === 'old' || saved === 'new')) {
        this.currentMobileLayout = saved;
      }
      // 如果没有保存的偏好，currentMobileLayout 保持 null，由调用方决定默认值
    } catch (e) {
      console.warn('无法读取移动端布局偏好:', e);
    }
  }

  /**
   * 保存移动端布局偏好
   */
  saveMobileLayoutPreference(layout) {
    try {
      localStorage.setItem(this.MOBILE_STORAGE_KEY, layout);
    } catch (e) {
      console.warn('无法保存移动端布局偏好:', e);
    }
  }

  /**
   * 切换布局（桌面端）
   * 同步更新移动端按钮状态：normal -> old, media -> new
   */
  switchLayout(targetLayout) {
    if (targetLayout !== 'normal' && targetLayout !== 'media') {
      console.warn('Invalid layout:', targetLayout);
      return;
    }
    
    if (this.currentLayout === targetLayout) return;

    this.currentLayout = targetLayout;
    this.applyLayout(targetLayout, true);
    this.saveLayoutPreference(targetLayout);

    // 同步移动端按钮选中状态
    const syncedMobileLayout = targetLayout === 'media' ? 'new' : 'old';
    this.currentMobileLayout = syncedMobileLayout;
    this.saveMobileLayoutPreference(syncedMobileLayout);
    // 仅更新移动端按钮 active 状态，不触发 applyMobileLayout（避免宽度判断拦截）
    this.mobileButtons = this.querySelectorAll('.yg-mobile-toggle .yg-layout-toggle-btn');
    this.mobileButtons.forEach(btn => {
      if (btn.dataset.mobileLayout === syncedMobileLayout) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
    // 同步 data-yg-mobile-layout 属性
    if (this.productGrid) {
      this.productGrid.setAttribute('data-yg-mobile-layout', syncedMobileLayout);
    }
  }

  /**
   * 切换移动端布局
   * 同步更新桌面端按钮状态：old -> normal, new -> media
   */
  switchMobileLayout(targetLayout) {
    if (targetLayout !== 'old' && targetLayout !== 'new') {
      console.warn('Invalid mobile layout:', targetLayout);
      return;
    }
    
    if (this.currentMobileLayout === targetLayout) return;

    this.currentMobileLayout = targetLayout;
    this.applyMobileLayout(targetLayout, true);
    this.saveMobileLayoutPreference(targetLayout);

    // 同步桌面端按钮选中状态
    const syncedLayout = targetLayout === 'new' ? 'media' : 'normal';
    this.currentLayout = syncedLayout;
    this.saveLayoutPreference(syncedLayout);
    // 仅更新桌面端按钮 active 状态，不触发 applyLayout（避免宽度判断拦截）
    this.buttons = this.querySelectorAll('.yg-desktop-toggle .yg-layout-toggle-btn');
    this.buttons.forEach(btn => {
      if (btn.dataset.layout === syncedLayout) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  /**
   * 应用布局（桌面端）
   * @param {string} layout - 'normal' 或 'media'
   * @param {boolean} animate - 是否显示动画
   */
  applyLayout(layout, animate = false) {
    const isDesktop = window.innerWidth >= 750;

    // 重新查询按钮（防止 AJAX 更新后按钮引用失效）
    this.buttons = this.querySelectorAll('.yg-desktop-toggle .yg-layout-toggle-btn');

    // 更新按钮激活状态
    this.buttons.forEach(btn => {
      if (btn.dataset.layout === layout) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    // 仅桌面端更新产品网格类名（避免覆盖移动端网格）
    if (isDesktop) {
      this.productGrid.setAttribute('data-yg-active-layout', layout);
      this.updateGridClasses(layout);
    }

    // 切换每个产品卡片的布局（移动端/桌面端都需要同步，避免筛选后样式错乱）
    const containers = this.productGrid.querySelectorAll('.yg-dual-layout-wrapper');
    
    containers.forEach(container => {
      const normalCard = container.querySelector('.yg-layout-card-normal');
      const mediaCard = container.querySelector('.yg-layout-card-media');

      if (!normalCard || !mediaCard) return;

      // 无动画直接切换
      container.setAttribute('data-yg-current-layout', layout);
      if (layout === 'normal') {
        normalCard.style.display = 'block';
        mediaCard.style.display = 'none';
      } else {
        normalCard.style.display = 'none';
        mediaCard.style.display = 'block';
      }
    });

    // 触发自定义事件，供其他组件监听
    this.dispatchEvent(new CustomEvent('layout-changed', {
      detail: { layout },
      bubbles: true
    }));
  }

  /**
   * 应用移动端布局
   * @param {string} layout - 'old' 或 'new'
   * @param {boolean} animate - 是否显示动画
   */
  applyMobileLayout(layout, animate = false) {
    // 桌面端不执行移动端布局切换
    if (window.innerWidth >= 750) {
      return;
    }

    // 重新查询按钮（防止 AJAX 更新后按钮引用失效）
    this.mobileButtons = this.querySelectorAll('.yg-mobile-toggle .yg-layout-toggle-btn');

    // 更新按钮激活状态
    this.mobileButtons.forEach(btn => {
      if (btn.dataset.mobileLayout === layout) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    // 给 product-grid 添加 data-yg-mobile-layout 属性
    this.productGrid.setAttribute('data-yg-mobile-layout', layout);

    // 触发自定义事件
    this.dispatchEvent(new CustomEvent('mobile-layout-changed', {
      detail: { layout },
      bubbles: true
    }));
  }

  /**
   * 更新产品网格的类名
   * @param {string} layout - 'normal' 或 'media'
   */
  updateGridClasses(layout) {
    if (layout === 'normal') {
      // Normal 布局：使用预生成的 normal 类名
      this.productGrid.className = this.normalGridClasses;
      this.productGrid.setAttribute('data-yg-active-layout', 'normal');
    } else {
      // Media 布局：使用预生成的 media 类名
      this.productGrid.className = this.mediaGridClasses;
      this.productGrid.setAttribute('data-yg-active-layout', 'media');
    }
  }
}

// 注册自定义元素
customElements.define('yg-layout-toggle', YGLayoutToggle);

// 监听 AJAX 分页/筛选加载，重新初始化
document.addEventListener('DOMContentLoaded', () => {
  // 使用 MutationObserver 监听 ProductGridContainer 的变化
  const observeProductGrid = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const observer = new MutationObserver((mutations) => {
      // 检查是否有子节点被替换
      const hasProductGridChanged = mutations.some(mutation => {
        return mutation.type === 'childList' && mutation.addedNodes.length > 0;
      });

      if (hasProductGridChanged) {
        const toggle = document.querySelector('yg-layout-toggle');
        if (toggle) {
          // 重新获取 productGrid 引用（因为 DOM 已更新）
          toggle.productGrid = document.querySelector('#product-grid');
          
          if (!toggle.productGrid) {
            console.warn('Product grid not found after update');
            return;
          }

          // 重新生成网格类名
          toggle.originalGridClasses = toggle.productGrid.className;
          toggle.generateGridClasses();

          // 重新应用当前布局到新加载的产品
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // 无论屏幕宽度，先直接设置 data-yg-mobile-layout 属性
              // 避免 CSS 选择器无法匹配导致移动端卡片样式异常
              if (toggle.currentMobileLayout) {
                toggle.productGrid.setAttribute('data-yg-mobile-layout', toggle.currentMobileLayout);
              }

              // 无论屏幕宽度都先同步 dual card 显示状态与按钮状态
              // （applyLayout 内部会自行判断是否更新桌面端网格类名）
              if (toggle.buttons.length && toggle.currentLayout) {
                toggle.applyLayout(toggle.currentLayout, false);
              }

              // 移动端额外应用 mobile layout（data-yg-mobile-layout + 移动端按钮状态）
              if (window.innerWidth < 750 && toggle.currentMobileLayout) {
                toggle.applyMobileLayout(toggle.currentMobileLayout, false);
              }
            });
          });
        }
      }
    });

    // 监听 ProductGridContainer 的子节点变化
    observer.observe(container, {
      childList: true,
      subtree: false
    });
  };

  observeProductGrid();
});

